import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CodingExamExaminerData, ExaminerProfile, MultipleChoiceExamExaminerData, RecruitingInterviewExaminerData } from '../store/workspace/types';

export function exportRecruitingInterviewTableToExcel(data: RecruitingInterviewExaminerData[]) {
    const columnKeys = [
        'culturalFit',
        'communicationSkills',
        'problemSolvingAbility',
        'teamCollaboration',
        'timeManagement',
        'selfMotivation',
        'multitaskingAbility',
        'taskPrioritization',
        'productivity',
    ];

    const columnLabels = [
        'Cultural Fit',
        'Communication Skills',
        'Problem-Solving Ability',
        'Team Collaboration',
        'Time Management',
        'Self Motivation',
        'Multitasking Ability',
        'Task Prioritization',
        'Productivity',
    ];

    const wsData: any[][] = [];

    wsData.push([
        'Name',
        ...columnLabels,
    ]);

    data.forEach(applicant => {
        const row = [];

        // Name
        row.push(`${applicant.first_name} ${applicant.last_name}`);

        // Evaluation columns
        columnKeys.forEach(key => {
            const matched = applicant.evaluatedCriteria?.find(crit => crit.nameKey === key);
            row.push(matched ? (matched.result === 'True' ? '✔️' : matched.result === 'False' ? '❌' : 'N/A') : 'In progress');
        });

        wsData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recruiting Interview');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'recruiting_interview_table.xlsx');
};

export function exportMultipleChoiceExamTableToExcel(data: MultipleChoiceExamExaminerData[]) {
    const wsData: any[][] = [];

    wsData.push([
        'Name', 'Status', 'Grade',
        'Question levels', '', '',
        'Question types', '', '',
    ]);

    wsData.push([
        'Name', 'Status', 'Grade',
        'Easy', 'Medium', 'Hard',
        'General', 'Technical', 'Coding',
    ]);

    data.forEach((info) => {
        wsData.push([
            `${info.first_name} ${info.family_name}`,
            info.status,
            `${info.scores.score_overall}/${info.scores.max_score}`,
            `${info.scores.score_easy_level}/${info.scores.n_questions_easy}`,
            `${info.scores.score_medium_level}/${info.scores.n_questions_medium}`,
            `${info.scores.score_hard_level}/${info.scores.n_questions_hard}`,
            `${info.scores.score_general}/${info.scores.n_questions_general}`,
            `${info.scores.score_technical}/${info.scores.n_questions_technical}`,
            `${info.scores.score_coding}/${info.scores.n_questions_coding}`,
        ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!merges'] = [
        // Row 0 merges for category headers
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Name
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Status
        { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // Grade
        { s: { r: 0, c: 3 }, e: { r: 0, c: 5 } }, // Question levels
        { s: { r: 0, c: 6 }, e: { r: 0, c: 8 } }, // Question types
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MCQ Exam');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'multiple_choice_exam_table.xlsx');
};

export function exportCodingExamTableToExcel(data: CodingExamExaminerData[]) {
    const wsData: any[][] = [];

    // Header rows
    wsData.push([
        'Name', 'Status', 'Grade', 'Q', 'Coverage', 'Time', 'Space', 'Time',
        'Readability', 'Duplication', 'Efficiency', 'Complexity',
        'Maintainability', 'Verification Constraint',
    ]);

    // Process each examiner
    data.forEach((q) => {
        const name = `${q.first_name} ${q.family_name}`;
        const status = q.status;
        const grade = `${q.grade}/10`;

        q.scores.forEach((score, i) => {
            const row: any[] = [];

            if (i === 0) {
                // First row for the user, insert name/status/grade
                row.push(name, status, grade);
            } else {
                // Leave blank for rowspan effect
                row.push('', '', '');
            }

            row.push(
                `Q${i + 1}`,
                score?.test_coverage ?? '',
                score?.execution_time?.score ?? '',
                score?.space_complexity?.score ?? '',
                score?.time_complexity?.score ?? '',
                score?.readability ?? '',
                score?.duplication ?? '',
                score?.efficiency ?? '',
                score?.complexity ?? '',
                score?.maintainability ?? '',
                score?.verifies_constraints ? '✔️' : '❌',
            );

            wsData.push(row);
        });
    });

    // Create worksheet and merge cells for rowSpan and colSpan
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    let rowPointer = 1;
    data.forEach(() => {
        // Merge name/status/grade over 3 rows
        ws['!merges'] = ws['!merges'] || [];
        ws['!merges'].push(
            { s: { r: rowPointer, c: 0 }, e: { r: rowPointer + 2, c: 0 } }, // Name
            { s: { r: rowPointer, c: 1 }, e: { r: rowPointer + 2, c: 1 } }, // Status
            { s: { r: rowPointer, c: 2 }, e: { r: rowPointer + 2, c: 2 } }  // Grade
        );
        rowPointer += 3;
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Coding Exam');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'coding_exam_table.xlsx');
};

// export function exportCodingExamTableToExcel(data: CodingExamExaminerData[]) {
//     const wsData: any[][] = [];

//     // 1️⃣ Top grouped headers
//     wsData.push([
//         'Name', 'Status', 'Grade',
//         'Test Cases', '',                 // Q, Coverage
//         'Complexity', '', '',             // Time, Space, Time
//         'Code Quality', '', '', '', '', '', // Readability ... Verification Constraint
//     ]);

//     // 2️⃣ Subheaders
//     wsData.push([
//         'Name', 'Status', 'Grade',
//         'Q', 'Coverage',
//         'Time', 'Space', 'Time',
//         'Readability', 'Duplication', 'Efficiency',
//         'Complexity', 'Maintainability', 'Verification Constraint'
//     ]);

//     let rowPointer = 2; // data rows start from row index 2

//     // 3️⃣ Data rows
//     data.forEach((q) => {
//         const name = `${q.first_name} ${q.family_name}`;
//         const status = q.status;
//         const grade = `${q.grade}/10`;

//         q.scores.forEach((score, i) => {
//             const row: any[] = [];

//             if (i === 0) {
//                 row.push(name, status, grade);
//             } else {
//                 row.push('', '', '');
//             }

//             row.push(
//                 `Q${i + 1}`,
//                 score?.test_coverage ?? '',
//                 score?.execution_time?.score ?? '',
//                 score?.space_complexity?.score ?? '',
//                 score?.time_complexity?.score ?? '',
//                 score?.readability ?? '',
//                 score?.duplication ?? '',
//                 score?.efficiency ?? '',
//                 score?.complexity ?? '',
//                 score?.maintainability ?? '',
//                 score?.verifies_constraints ? '✔️' : '❌',
//             );

//             wsData.push(row);
//         });

//         // 4️⃣ Merge name/status/grade cells across question rows (assuming 3 questions)
//         const scoreCount = q.scores.length;
//         if (scoreCount > 1) {
//             for (let col = 0; col <= 2; col++) {
//                 ws['!merges'] = ws['!merges'] || [];
//                 ws['!merges'].push({
//                     s: { r: rowPointer, c: col },
//                     e: { r: rowPointer + scoreCount - 1, c: col }
//                 });
//             }
//         }

//         rowPointer += scoreCount;
//     });

//     // 5️⃣ Create worksheet and add merged headers
//     const ws = XLSX.utils.aoa_to_sheet(wsData);

//     // Add top header merges
//     ws['!merges'] = ws['!merges'] || [];
//     ws['!merges'].push(
//         // Group headers
//         { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Name
//         { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Status
//         { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // Grade
//         { s: { r: 0, c: 3 }, e: { r: 0, c: 4 } }, // Test Cases
//         { s: { r: 0, c: 5 }, e: { r: 0, c: 7 } }, // Complexity
//         { s: { r: 0, c: 8 }, e: { r: 0, c: 13 } } // Code Quality
//     );

//     // 6️⃣ Finalize and download
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Coding Exam');

//     const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
//     const blob = new Blob([wbout], { type: 'application/octet-stream' });
//     saveAs(blob, 'coding_exam_table.xlsx');
// }

export function exportPersonalInterviewTableToExcel(data: ExaminerProfile[]) {
    const wsData: any[][] = [];

    wsData.push([
        'Name',
        'Status',
        'Email',
        'Phone Number',
        'Location',
    ]);

    data.forEach((examiner) => {
        wsData.push([
            `${examiner.first_name} ${examiner.family_name}`,
            examiner.status,
            examiner.email,
            examiner.phone_number,
            `${examiner.city} / ${examiner.state}`,
        ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Personal Interview');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'personal_interview_table.xlsx');
};

export function exportFinalResultTableToExcel(data: ExaminerProfile[]) {
    const wsData: any[][] = [];

    wsData.push([
        'Name',
        'Status',
        'Interview Status',
    ]);

    data.forEach((examiner) => {
        wsData.push([
            `${examiner.first_name} ${examiner.family_name}`,
            examiner.status,
            '',
        ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Final Result');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'final_result_table.xlsx');
};
